import { View } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import Scroll from 'src/game/components/shared/Scroll';
import {
  FriendListItem,
  FriendListPlayerParams,
} from '../components/FriendListItem';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  getFriendshipPendingRequests,
  getFriendshipPendingRequestsSent,
} from 'src/replicant/friends/friends.getters';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { acceptFriendRequest } from '../friends.sequences';
import { fetchFriendList } from '../friends.utils';
import { FriendSuggestionsSection } from '../friendSuggestions/friendSuggestions.section';
import {
  createSuggestionChromeView,
  FriendSuggestionsListItem,
} from '../friendSuggestions/friendSuggestions.items';
import { removeClusterSuggestionFromCache } from '../friendSuggestions/friendSuggestions.utils';
import { openUserProfilePopup } from '../friendsList.sequences';
import features from 'src/replicant/ruleset/features';

type TabRequestsOpts = {
  tab: View;
  closePopup: () => void;
};

export class TabRequests {
  private tab: View;
  private scroll: Scroll<FriendSuggestionsListItem, View>;
  private noRequestsLabel: LangBitmapFontTextView;
  private closePopup: () => void;
  private suggestions = new FriendSuggestionsSection('friend_request', () =>
    // Preserve the scroll position when re-rendering after an Add tap.
    this.refresh({ preserveScroll: true }),
  );
  // Cluster suggestion eligibility is fixed for this instance, like TabMyFriends.
  private readonly suggestionsEnabled =
    features(user()).friendListClusterSuggestions;

  constructor(opts: TabRequestsOpts) {
    this.tab = opts.tab;
    this.closePopup = opts.closePopup;

    this.scroll = new Scroll({
      superview: this.tab,
      createItem: (
        superview: View,
        _i: number,
        data: FriendSuggestionsListItem,
      ) => {
        const chrome = createSuggestionChromeView(superview, data);
        if (chrome) return chrome;
        const item = new FriendListItem({ superview });
        item.setup(data as FriendListPlayerParams);
        return item;
      },
      showBg: true,
      rect: {
        x: 0,
        y: 0,
        width: this.tab.style.width,
        height: this.tab.style.height,
      },
    });

    // Empty state for requests
    this.noRequestsLabel = new LangBitmapFontTextView({
      superview: this.tab,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: this.tab.style.width - 60,
      height: 200,
      color: '#AE7E4A',
      centerOnOrigin: true,
      x: this.tab.style.width / 2,
      y: this.tab.style.height / 2,
      localeText: () => i18n('friendsList.noRequests'),
    });
  }

  private async handleOnTap(
    id: string,
    profileFallback: { name: string; photo: string },
  ): Promise<void> {
    const actionTaken = await openUserProfilePopup(
      id,
      'request_tab',
      profileFallback,
    );

    // If visit started, close this popup too
    if (actionTaken === 'visit') {
      this.closePopup();
      return;
    }

    // If friend request was sent from the profile, remove from suggestions cache
    if (actionTaken === 'addFriend') {
      removeClusterSuggestionFromCache(id);
    }

    // Refresh the list in case state changed
    this.refresh();
  }

  private async handleOnAccept(id: string): Promise<void> {
    const request = this.getRequest(id);
    if (!request) {
      await openPopupPromise('popupError', {
        message: i18n('match2.team.errors.tryAgain'),
      });
      return;
    }
    const ok = await acceptFriendRequest(request, 'hud');
    if (ok) {
      // Invalidate friend list cache to include the new friend when refreshed
      await fetchFriendList().invalidate();
      this.refresh();
    }
  }

  private getRequest(id: string): FriendshipRequest | undefined {
    return user().friendshipRequests[id] as FriendshipRequest | undefined;
  }

  private getRequestItems(): FriendListPlayerParams[] {
    const requestsReceived = getFriendshipPendingRequests(user()).map(
      (request) => this.mapProfileToParams(request.owner),
    );

    const requestsSent = getFriendshipPendingRequestsSent(user()).map(
      (request) => this.mapProfileToParams(request.target, true),
    );

    return [...requestsReceived, ...requestsSent];
  }

  /** Reset per-open suggestion state. Called from the popup's init(). */
  public resetSuggestions() {
    this.suggestions.reset();
  }

  public async refresh(opts: { preserveScroll?: boolean } = {}) {
    const requestItems = this.getRequestItems();
    this.scroll.setItems(requestItems, !opts.preserveScroll);
    this.noRequestsLabel.updateOpts({ visible: requestItems.length === 0 });
    await this.appendSuggestions();
  }

  /**
   * Append the "You may know" section below the pending requests (always shown
   * on this tab when the feature is enabled). Requests are re-read after the fetch
   * so an accept/decline during the await can't paint a stale list.
   */
  private async appendSuggestions(): Promise<void> {
    if (!this.suggestionsEnabled) return;

    const { items } = await this.suggestions.fetchParams();
    if (items.length === 0) return;

    const combined = this.suggestions.buildRows(this.getRequestItems(), items);
    this.scroll.setItems(combined, false);
    this.noRequestsLabel.updateOpts({ visible: false });
  }

  private mapProfileToParams(
    profile: FriendshipRequest['owner'],
    requestSent = false,
  ): FriendListPlayerParams {
    return {
      id: profile.id,
      name: profile.name,
      profilePicture: profile.profilePicture,
      frame: profile.frame,
      species: profile.species,
      requestSent,
      onTap: () =>
        this.handleOnTap(profile.id, {
          name: profile.name,
          photo: profile.profilePicture,
        }),
      onAccept: requestSent ? undefined : () => this.handleOnAccept(profile.id),
    };
  }
}
